/**
 * yards + dispatcher_yard_assignments (DEC-011 / master spec §86).
 *
 * The LOCATION side of the YARDS-TRUCKS-TRAILERS triad. Yards are first-
 * class hauler-tenant entities (home-base locations) and the source of
 * truth for the home-base GPS coordinate; dispatcher visibility into a
 * subset of a tenant's yards is controlled exclusively by the
 * dispatcher_yard_assignments junction table — there is NO tenant feature
 * flag named `multi_yard_dispatch` (DEC-011 explicit veto).
 *
 * Tables (2 total):
 *   - yards: tenant-scoped, RLS-enforced. Holds name + address + GPS +
 *     geofence_radius_ft (default 500). is_primary boolean tracks the
 *     hauler's default yard; defensive partial UNIQUE prevents two
 *     primary yards per tenant.
 *   - dispatcher_yard_assignments: composite-PK junction (user_id, yard_id)
 *     with is_primary flag. RLS via subquery — yard_id must resolve to a
 *     yard in the current tenant.
 *
 * Master spec §86 cross-references:
 *   - site_geofences.site_type='YARD' references yards.yard_id (Plan 02-03
 *     sites.ts) for richer polygon geofences. The simple radius lives on
 *     yards (gps_lat/gps_lng/geofence_radius_ft); site_geofences is for
 *     the editor-driven polygon case.
 *   - drivers.home_yard_id / drivers.current_yard_id (Plan 02-02 drivers.ts) —
 *     soft uuid columns; FK constraint added in Plan 02-04 relations.ts.
 *   - shifts.started_at_yard_id / shifts.ended_at_yard_id (Plan 02-02 drivers.ts) —
 *     soft uuid columns; FK constraint added in Plan 02-04 relations.ts.
 *   - trucks.current_yard_id / trucks.home_yard_id (Plan 02-02 drivers.ts) —
 *     soft uuid columns; FK constraint added in Plan 02-04 relations.ts.
 *   - trailers.current_yard_id / trailers.home_yard_id (Plan 02-03 trailers.ts) —
 *     `.references(() => yards.id)` declared inline in trailers.ts because
 *     yards.ts and trailers.ts ship in the same plan (02-03) and import
 *     order is well-defined within a plan.
 *
 * Hauler-only constraint: yards.tenant_id and dispatcher_yard_assignments
 * (via the yards subquery) reference the unified `tenants` table per
 * CONTEXT D-04. Hauler-only enforcement is the role-guard middleware's
 * responsibility (Phase 3); no schema-level CHECK trigger here. Documented
 * in DEC-011 / D-19.
 *
 * RLS posture (CONTEXT D-15):
 *   - yards: standard tenant-scoped predicate.
 *   - dispatcher_yard_assignments: subquery predicate — yard_id IN
 *     (SELECT yard_id FROM yards WHERE tenant_id = ...). The junction has
 *     no tenant_id column of its own; tenant scoping is derived from the
 *     yard_id FK.
 *
 * NEGATIVE NOTE — `multi_yard_dispatch` is NOT a column anywhere
 * (DEC-011 sweep). Yard visibility for dispatchers is controlled
 * exclusively by `dispatcher_yard_assignments`. Do NOT add
 * `multi_yard_dispatch BOOLEAN` to tenants or any other table even if a
 * future feature request asks for it — the answer is "assign the
 * dispatcher to all yards" via dispatcher_yard_assignments.
 *
 * Sources:
 *   - master spec §86 (YARDS, TRUCKS, AND TRAILERS — DEC-011 verbatim spec)
 *   - master spec §10 (DRIVER SHIFT OPERATIONS — yard at shift start/end)
 *   - master spec §15 (GEOFENCING — yard polygon link to site_geofences)
 *   - DEC-011 (Yards — Core Platform Feature; user-authored)
 *   - CONTEXT D-19 (Phase 2 schema addendum recording DEC-011)
 *   - CONTEXT D-04 (unified tenants table; hauler-only via role-guard, not schema)
 *
 * Locked contracts (future phases must NOT change without DEC-NNN):
 *   - yards is the source of truth for hauler home-base locations; the
 *     intrinsic gps_lat/gps_lng/geofence_radius_ft columns are the simple-
 *     radius case. site_geofences with site_type='YARD' is the polygon case.
 *   - dispatcher_yard_assignments is the ONLY mechanism for restricting
 *     dispatcher visibility to a subset of a tenant's yards.
 *   - At most one primary yard per tenant (defensive partial UNIQUE).
 *   - At most one primary yard assignment per user (defensive partial UNIQUE).
 */

import {
  boolean,
  foreignKey,
  index,
  integer,
  numeric,
  pgPolicy,
  pgTable,
  primaryKey,
  timestamp,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { importBatches } from './imports.js';

/**
 * yards — tenant-scoped hauler home-base locations (DEC-011 / §86).
 *
 * Column name on the PK is `yard_id` per the master spec §86 SQL spec;
 * Drizzle's pgTable maps it to the inferred-type field `id`. References to
 * `yards.yard_id` elsewhere in the spec resolve to this column.
 *
 * RLS-enforced. T-02-03-01 family (HIGH) — cross-tenant visibility blocked
 * by RLS; proven by Plan 02-06's RLS-isolation test.
 *
 * Indexes (master spec §86 explicit):
 *   - (tenant_id) — tenant's yard list.
 *   - PARTIAL UNIQUE (tenant_id) WHERE is_primary = true — at most one
 *     primary yard per tenant (defensive; the role-guard middleware in
 *     Phase 3 also enforces but the schema makes it impossible to drift).
 */
export const yards = pgTable(
  'yards',
  {
    id: uuid('yard_id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // E.g. "Elk City Yard", "Hinton Yard".
    name: varchar('name').notNull(),
    address: varchar('address'),
    gpsLat: numeric('gps_lat', { precision: 10, scale: 7 }),
    gpsLng: numeric('gps_lng', { precision: 10, scale: 7 }),
    // Master spec §86 default 500ft simple-radius geofence.
    geofenceRadiusFt: integer('geofence_radius_ft').notNull().default(500),
    isPrimary: boolean('is_primary').notNull().default(false),
    isActive: boolean('is_active').notNull().default(true),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // Task 20.5 provenance marker — set only by the import commit engine (20.6).
    // NULL means "not created by an import", which is every row today.
    importBatchId: uuid('import_batch_id'),
  },
  (t) => [
    // Task 20.5 — per-batch reconciliation and undo. Partial: almost no row
    // is import-created, so a full index would be mostly NULLs.
    index('yards_import_batch_idx')
      .on(t.tenantId, t.importBatchId)
      .where(sql`import_batch_id IS NOT NULL`),
    // Task 20.6a / DEC-265 — composite so a row cannot claim ANOTHER tenant's
    // batch. Declared here rather than as a column .references() because the
    // reference spans two columns. One detail Drizzle cannot express: the
    // migration uses ON DELETE SET NULL ("import_batch_id"), the Postgres 15
    // column-scoped form, because a bare SET NULL would also null tenant_id
    // (NOT NULL) and make deleting a batch fail. 0107 is authoritative.
    foreignKey({
      columns: [t.tenantId, t.importBatchId],
      foreignColumns: [importBatches.tenantId, importBatches.id],
      name: 'yards_import_batch_tenant_fk',
    }).onDelete('set null'),
    // Master spec §86 explicit: CREATE INDEX ON yards (tenant_id).
    index('yards_tenant_idx').on(t.tenantId),
    // Defensive partial UNIQUE — at most one is_primary=true per tenant.
    uniqueIndex('yards_tenant_primary_idx')
      .on(t.tenantId)
      .where(sql`is_primary = true`),
    pgPolicy('yards_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type Yard = typeof yards.$inferSelect;
export type NewYard = typeof yards.$inferInsert;
export const yardSelectSchema = createSelectSchema(yards);
export const yardInsertSchema = createInsertSchema(yards);

/**
 * dispatcher_yard_assignments — junction table (DEC-011 / §86).
 *
 * Composite PK (user_id, yard_id). RLS via subquery — yard_id must resolve
 * to a yard in the current tenant. is_primary tracks the user's default
 * yard view in the dispatcher UI; defensive partial UNIQUE prevents
 * multiple primary assignments per user.
 *
 * Indexes (master spec §86 explicit):
 *   - (yard_id) — list users assigned to a yard.
 *   - (user_id) — list yards a user can see.
 *   - PARTIAL UNIQUE (user_id) WHERE is_primary = true — defensive.
 *
 * RLS subquery rationale: this junction table has no tenant_id column of
 * its own — its tenant scoping derives from the yards row it references.
 * The subquery
 *   `yard_id IN (SELECT yard_id FROM yards WHERE tenant_id = current_setting(...))`
 * limits the visible rows to assignments whose yard belongs to the current
 * tenant. The referenced yards table itself is RLS-protected; the subquery
 * runs as the current role (app_user), so cross-tenant yard ids are
 * filtered out at the inner SELECT before reaching the outer policy.
 */
export const dispatcherYardAssignments = pgTable(
  'dispatcher_yard_assignments',
  {
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    yardId: uuid('yard_id')
      .notNull()
      .references(() => yards.id, { onDelete: 'cascade' }),
    isPrimary: boolean('is_primary').notNull().default(false),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({ columns: [t.userId, t.yardId] }),
    // Master spec §86 explicit indexes.
    index('dispatcher_yard_assignments_yard_idx').on(t.yardId),
    index('dispatcher_yard_assignments_user_idx').on(t.userId),
    // Defensive partial UNIQUE — at most one primary assignment per user.
    uniqueIndex('dispatcher_yard_assignments_user_primary_idx')
      .on(t.userId)
      .where(sql`is_primary = true`),
    pgPolicy('dispatcher_yard_assignments_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`yard_id IN (SELECT yard_id FROM yards WHERE tenant_id = current_setting('app.current_tenant_id')::uuid)`,
    }),
  ],
);

export type DispatcherYardAssignment = typeof dispatcherYardAssignments.$inferSelect;
export type NewDispatcherYardAssignment = typeof dispatcherYardAssignments.$inferInsert;
export const dispatcherYardAssignmentSelectSchema = createSelectSchema(dispatcherYardAssignments);
export const dispatcherYardAssignmentInsertSchema = createInsertSchema(dispatcherYardAssignments);
